/* ============================================================
   ATELIER - Product page (Phase 1: shell)
   Drives only the reskinned bits; the gallery (keen-slider),
   variant switching, ATC, qty and the AJAX colour-swatch swap are
   all handled by the theme's existing main.min.js / product-info.js.

   IMPORTANT: the colour-swatch AJAX swap (main.js) replaces chunks of
   DOM in place (#productAccordion innerHTML, the h1's textContent, the
   .colour-swatches innerHTML, gallery img src). So everything here is
   built to survive that:
     - accordion clicks use event delegation (work on swapped-in nodes)
     - the open panel is re-applied after a swap (CSS fallback + a
       MutationObserver re-sync)
   ============================================================ */
(function () {
  "use strict";

  /* ---- sticky gallery offset (mirrors the header's published height) ---- */
  var STICKY_GAP = 24;
  function syncStickyTop() {
    var sp = parseFloat(
      getComputedStyle(document.documentElement).scrollPaddingTop
    );
    if (!sp || isNaN(sp)) return;
    Array.prototype.slice
      .call(document.querySelectorAll(".atelier-pdp .apg-media"))
      .forEach(function (m) {
        m.style.setProperty("--apg-sticky-top", sp + STICKY_GAP + "px");
      });
  }

  /* ---- Editorial-Ledger accordion ----
     Delegated so it keeps working after the AJAX swap replaces
     #productAccordion's contents. JS sets an exact max-height for a
     smooth animation; the CSS rule .apg-bl-it.open .apg-bl-a{max-height}
     is the fallback that keeps the open panel expanded immediately after
     a swap (before any click). */
  function panelOf(item) {
    return item ? item.querySelector(".apg-bl-a") : null;
  }
  function setItem(item, open) {
    var p = panelOf(item);
    if (!p) return;
    if (open) {
      item.classList.add("open");
      p.style.maxHeight = p.scrollHeight + "px";
    } else {
      if (item.classList.contains("open") && !p.style.maxHeight) {
        /* Panel is running on the CSS fallback cap (2000px - fresh markup
           after an AJAX swap). Pin the real height first so the close
           animates from where it visually is; animating from the cap plays
           the whole visible collapse in the last split-second (page lurch). */
        p.style.maxHeight = p.scrollHeight + "px";
        void p.offsetHeight;
      }
      item.classList.remove("open");
      p.style.maxHeight = "0px";
    }
  }
  function syncOpenPanels(scope) {
    Array.prototype.slice
      .call((scope || document).querySelectorAll(".atelier-pdp .apg-bl-it.open"))
      .forEach(function (it) {
        var p = panelOf(it);
        if (p) p.style.maxHeight = p.scrollHeight + "px";
      });
  }

  /* Open panels store an exact max-height measured when they open (or at
     init); images inside (description photos, WELS logos) can finish loading
     later and grow the content past that cap, clipping the panel bottom.
     Image load events do not bubble but they DO capture. */
  document.addEventListener("load", function (e) {
    var t = e.target;
    if (t && t.tagName === "IMG" && t.closest && t.closest(".atelier-pdp .apg-bl-a")) {
      syncOpenPanels();
    }
  }, true);

  document.addEventListener("click", function (e) {
    var q = e.target.closest && e.target.closest(".atelier-pdp .apg-bl-q");
    if (!q) return;
    var item = q.closest(".apg-bl-it");
    var acc = q.closest(".apg-bl");
    if (!item || !acc) return;
    var wasOpen = item.classList.contains("open");

    /* Rendered height of any open panel ABOVE the clicked row. Closing it
       pulls the row up by exactly this much once the transitions settle -
       without compensation the page just lurches by that amount (worst on
       the very first click, which always collapses the server-open
       Description). offsetHeight is the clipped/rendered height, so this
       stays exact even mid-animation or with late-loading images. */
    var shiftUp = 0;
    var rows = Array.prototype.slice.call(acc.querySelectorAll(".apg-bl-it"));
    for (var i = 0; i < rows.length; i++) {
      if (rows[i] === item) break;
      if (rows[i].classList.contains("open")) {
        var above = panelOf(rows[i]);
        if (above) shiftUp += above.offsetHeight;
      }
    }
    var rowTop = item.getBoundingClientRect().top + window.scrollY;

    Array.prototype.slice
      .call(acc.querySelectorAll(".apg-bl-it"))
      .forEach(function (o) { setItem(o, false); });
    if (!wasOpen) setItem(item, true);

    if (!wasOpen && shiftUp > 0) {
      /* Deliberate version of the scroll the collapse used to fake: dock the
         clicked row just below the stuck header, at its FINAL position.
         window.scrollTo ignores the header's html scroll-padding-top, so
         apply it ourselves (same source syncStickyTop reads). */
      var pad = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop);
      if (!pad || isNaN(pad)) pad = 132; /* header JS sets it inline; 132 = desktop stuck height */
      var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({
        top: Math.max(0, rowTop - shiftUp - pad - 18),
        behavior: reduce ? "auto" : "smooth"
      });
    }
  });

  /* ---- finish-swatch shimmer (delegated so it survives swatch re-render) ---- */
  document.addEventListener("mouseover", function (e) {
    var el = e.target.closest && e.target.closest(".atelier-pdp .apg-fin-it");
    if (!el || el.__shimmering) return;
    el.__shimmering = true;
    el.classList.remove("play");
    void el.offsetWidth;
    el.classList.add("play");
    setTimeout(function () { el.__shimmering = false; }, 300);
  });

  /* (the bold-last-word title treatment and its post-swap re-bolder were
     removed 2026-07-05 - titles render at uniform weight) */

  function init(root) {
    if (!root || root.__atelierPdpInit) return;
    root.__atelierPdpInit = true;

    syncStickyTop();
    window.addEventListener("load", syncStickyTop);
    window.addEventListener("resize", syncStickyTop);

    syncOpenPanels(root);
    window.addEventListener("load", function () { syncOpenPanels(root); });
    window.addEventListener("resize", function () { syncOpenPanels(root); });

    /* Re-apply the open panel after the AJAX swap mutates the info column
       (it replaces #productAccordion contents). */
    var pi = root.matches("product-info") ? root : root.closest("product-info");
    var target = pi || root;
    var scheduled = false;
    var obs = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        syncOpenPanels(target);
      });
    });
    obs.observe(target, { childList: true, subtree: true });
  }

  /* ---- Atelier variant picker (Concept A "Atelier Select") ----
     Delegated so it survives product-info.js replacing the whole
     <variant-selects> subtree on every change. Selecting a custom row sets
     the hidden native <select> and dispatches `change`; Dawn (VariantSelects
     + product-info.js) then updates the main price, gallery, SKU and URL -
     we never duplicate that logic. The panel re-renders closed after a swap. */
  function closeAllPickers(except) {
    Array.prototype.slice
      .call(document.querySelectorAll(".atelier-pdp .apg-vp-sel.open"))
      .forEach(function (o) {
        if (o === except) return;
        o.classList.remove("open");
        var f = o.querySelector(".apg-vp-field");
        if (f) f.setAttribute("aria-expanded", "false");
      });
  }

  document.addEventListener("click", function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    /* open / close a picker */
    var field = t.closest(".atelier-pdp .apg-vp-field");
    if (field) {
      var sel = field.closest(".apg-vp-sel");
      var willOpen = !sel.classList.contains("open");
      closeAllPickers(sel);
      sel.classList.toggle("open", willOpen);
      field.setAttribute("aria-expanded", String(willOpen));
      return;
    }

    /* choose a row -> proxy to the hidden native <select> */
    var row = t.closest(".atelier-pdp .apg-vp-row");
    if (row) {
      if (row.getAttribute("aria-disabled") === "true") return;
      var picker = row.closest(".apg-vp-sel");
      if (picker) picker.classList.remove("open");
      var axis = row.closest("[data-axis]");
      var nativeSel = axis && axis.querySelector(".apg-vp-native");
      var idx = parseInt(row.getAttribute("data-index"), 10);
      if (nativeSel && !isNaN(idx) && nativeSel.options[idx]) {
        nativeSel.selectedIndex = idx;
        nativeSel.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return;
    }

    /* click anywhere else -> close open pickers */
    if (!t.closest(".atelier-pdp .apg-vp-sel")) closeAllPickers(null);
  });

  /* close on Escape */
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") closeAllPickers(null);
  });

  function boot() {
    Array.prototype.slice
      .call(document.querySelectorAll(".atelier-pdp"))
      .forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
